import type {
  TrainingGpuUsageShare,
  TrainingGpuWorkspaceUsage,
  TrainingGpuWorkspaceUsageRow,
} from "@/api";

/**
 * 쿼터 표의 기간 사용량 모델. workspace 사용량 응답을 행이 쓰는 모양으로 바꾸는 순수 함수다.
 *
 * 사용량은 workspace 축이다. 사람 행의 값은 그 사람이 지금 선점한 workspace 들의 합이다. 이 합은
 * 화면이 만든다. 서버는 사람 축 사용량을 주지 않는다. 지난 기간의 사용량을 지금 선점자에게
 * 귀속시키는 것은 화면의 표시 선택이기 때문이다.
 */

/** 한 행(workspace 하나 또는 사람 하나)의 기간 사용량. */
export type GpuQuotaRowUsage = {
  gpuHours: number;
  // 사용분에 단가를 곱한 값(USD)이다. 서버에 단가가 없으면 null 이다. 0 은 실제로 쓰지 않은 것이다.
  cost: number | null;
  sessionCount: number;
  // 기간의 UTC 일 순서다. 길이는 기간의 일 수와 같다. 사용이 없는 날은 0 이다.
  dailyGpuHours: readonly number[];
  // 작업 종류별 몫이다. 몫이 큰 순이다.
  byType: readonly TrainingGpuUsageShare[];
};

/** 사용량 응답에서 행을 만들 때 필요한 것만 모은 색인. */
export type GpuQuotaUsageIndex = {
  byWorkspace: ReadonlyMap<string, TrainingGpuWorkspaceUsageRow>;
  // 기간의 UTC 일 수다. 사용이 없는 행의 일별 값 길이를 정한다.
  periodCount: number;
  // 서버가 비용을 주는지 나타낸다. false 이면 모든 행의 비용이 null 이다.
  priced: boolean;
};

export function indexGpuQuotaUsage(usage: TrainingGpuWorkspaceUsage): GpuQuotaUsageIndex {
  return {
    byWorkspace: new Map(usage.workspaces.map((row) => [row.workspace_id, row])),
    periodCount: usage.periods.length,
    priced: usage.total_cost !== null,
  };
}

// 기간 안에 사용이 없는 workspace 의 값이다. 서버는 그 workspace 를 응답에 넣지 않는다.
// 비용은 단가가 있을 때만 0 이다. 단가가 없으면 "쓰지 않음" 이 아니라 "값 없음" 이다.
function emptyRowUsage(index: GpuQuotaUsageIndex): GpuQuotaRowUsage {
  return {
    gpuHours: 0,
    cost: index.priced ? 0 : null,
    sessionCount: 0,
    dailyGpuHours: Array.from({ length: index.periodCount }, () => 0),
    byType: [],
  };
}

export function workspaceRowUsage(
  index: GpuQuotaUsageIndex,
  workspaceId: string,
): GpuQuotaRowUsage {
  const row = index.byWorkspace.get(workspaceId);
  if (row === undefined) {
    return emptyRowUsage(index);
  }
  return {
    gpuHours: row.gpu_hours,
    cost: row.cost,
    sessionCount: row.session_count,
    dailyGpuHours: row.daily_gpu_hours,
    byType: row.by_type,
  };
}

// 사람 행의 사용량이다. 그 사람이 선점한 workspace 들의 합이다. 세션은 workspace 하나에만 속한다.
// 그래서 Session 수도 그대로 더한다.
export function sumRowUsage(
  index: GpuQuotaUsageIndex,
  usages: readonly GpuQuotaRowUsage[],
): GpuQuotaRowUsage {
  const total = emptyRowUsage(index);
  const dailyGpuHours = [...total.dailyGpuHours];
  const shares = new Map<string, { gpuHours: number; cost: number }>();
  let gpuHours = 0;
  let cost = 0;
  let sessionCount = 0;
  for (const usage of usages) {
    gpuHours += usage.gpuHours;
    cost += usage.cost ?? 0;
    sessionCount += usage.sessionCount;
    usage.dailyGpuHours.forEach((hours, day) => {
      dailyGpuHours[day] = (dailyGpuHours[day] ?? 0) + hours;
    });
    for (const share of usage.byType) {
      const sum = shares.get(share.key) ?? { gpuHours: 0, cost: 0 };
      sum.gpuHours += share.gpu_hours;
      sum.cost += share.cost ?? 0;
      shares.set(share.key, sum);
    }
  }
  return {
    gpuHours,
    cost: index.priced ? cost : null,
    sessionCount,
    dailyGpuHours,
    byType: [...shares.entries()]
      .map(([key, sum]) => ({
        key,
        gpu_hours: sum.gpuHours,
        cost: index.priced ? sum.cost : null,
      }))
      .sort((a, b) => b.gpu_hours - a.gpu_hours || a.key.localeCompare(b.key)),
  };
}
